import { makeAutoObservable, runInAction } from 'mobx'
import { logger, NON_DIGIT_REGEX } from 'shared/lib'
import { type INormalizedApiError } from 'shared/api'
import {
  type IRcsUseCaseStepParams,
  type IResponseEventRcsVideoUploaded,
  type IResponseEventRcsVideoUploadError,
  type IResponseRcsCompliance,
  RcsApi,
} from 'entities/Rcs'
import { type RcsWizardStore } from 'pages/settings/pages/rcsV2/store/rcsWizardStore'

const DESCRIPTION_MIN_LENGTH = 40
const DESCRIPTION_MAX_LENGTH = 1000
const TRAFFIC_MAX = 1_000_000_000
const TRAFFIC_MAX_DIGITS = 10
const TRAFFIC_VOLUME_MIN = 1
const SHORTCODE_MIN_LENGTH = 4
const SHORTCODE_MAX_LENGTH = 8
const VIDEO_MAX_SIZE_BYTES = 100 * 1024 * 1024
const VIDEO_ALLOWED_TYPES = ['video/mp4', 'video/quicktime']
const VIDEO_ALLOWED_EXTENSIONS = ['mp4', 'mov']
const VIDEO_TYPE_ERROR = {
  title: 'The file is not supported for upload',
  desc: 'Please upload only: mp4, mov',
}
const VIDEO_SIZE_ERROR = {
  desc: 'The video file is too large. It must not exceed 100 MB',
}
const VIDEO_UPLOAD_ERROR = {
  desc: 'Failed to upload the use case video',
}
const VIDEO_LIMIT_ERROR = {
  desc: 'You can upload only 1 video. Remove the current one first.',
}

type IVideoError = { title?: string; desc: string }

export class UseCaseStore {
  private _description = ''
  private _descriptionError = ''
  private _video: File | null = null
  private _videoUrl = ''
  private _videoError: IVideoError | null = null
  private _videoLoading = false
  private _pendingVideoToken = ''
  private _useCase = ''
  private _useCaseError = ''
  private _monthlyTraffic = ''
  private _monthlyTrafficError = ''
  private _hasShortcodeTraffic = ''
  private _hasShortcodeTrafficError = ''
  private _shortcode = ''
  private _shortcodeError = ''
  private _shortcodeTraffic = ''
  private _shortcodeTrafficError = ''
  private _trafficVolume = ''
  private _trafficVolumeError = ''

  constructor(private _wizardStore: RcsWizardStore) {
    makeAutoObservable(this)
  }

  get description() {
    return this._description
  }

  get descriptionError() {
    return this._descriptionError
  }

  get video() {
    return this._video
  }

  get videoUrl() {
    return this._videoUrl
  }

  get videoError() {
    return this._videoError
  }

  get videoLoading() {
    return this._videoLoading
  }

  get isUsFlow() {
    return this._wizardStore.isUsFlow
  }

  get useCase() {
    return this._useCase
  }

  get useCaseError() {
    return this._useCaseError
  }

  get useCaseOptions() {
    return this._wizardStore.useCaseOptions
  }

  get useCasesLoading() {
    return this._wizardStore.useCasesLoading
  }

  get monthlyTraffic() {
    return this._formatThousands(this._monthlyTraffic)
  }

  get monthlyTrafficError() {
    return this._monthlyTrafficError
  }

  get hasShortcodeTraffic() {
    return this._hasShortcodeTraffic
  }

  get hasShortcodeTrafficError() {
    return this._hasShortcodeTrafficError
  }

  get isShortcodeFlow() {
    return this._hasShortcodeTraffic === 'yes'
  }

  get shortcode() {
    return this._shortcode
  }

  get shortcodeError() {
    return this._shortcodeError
  }

  get shortcodeTraffic() {
    return this._formatThousands(this._shortcodeTraffic)
  }

  get shortcodeTrafficError() {
    return this._shortcodeTrafficError
  }

  get trafficVolume() {
    return this._formatThousands(this._trafficVolume)
  }

  get trafficVolumeError() {
    return this._trafficVolumeError
  }

  private get _isDescriptionValid() {
    const { length } = this._description
    return length >= DESCRIPTION_MIN_LENGTH && length <= DESCRIPTION_MAX_LENGTH
  }

  private get _isVideoValid() {
    return Boolean(this._videoUrl)
  }

  private get _isUseCaseValid() {
    return !this.isUsFlow || this._useCase.trim().length > 0
  }

  private get _isMonthlyTrafficValid() {
    return !this.isUsFlow || this._isTrafficValueValid(this._monthlyTraffic, 0)
  }

  private get _isHasShortcodeValid() {
    return !this.isUsFlow || this._hasShortcodeTraffic !== ''
  }

  private get _isShortcodeValid() {
    if (!this.isUsFlow || !this.isShortcodeFlow) return true
    const { length } = this._shortcode
    return length >= SHORTCODE_MIN_LENGTH && length <= SHORTCODE_MAX_LENGTH
  }

  private get _isShortcodeTrafficValid() {
    if (!this.isUsFlow || !this.isShortcodeFlow) return true
    return this._isTrafficValueValid(this._shortcodeTraffic, 0)
  }

  private get _isTrafficVolumeValid() {
    return !this.isUsFlow || this._isTrafficValueValid(this._trafficVolume, TRAFFIC_VOLUME_MIN)
  }

  get descriptionMinLength() {
    return DESCRIPTION_MIN_LENGTH
  }

  get descriptionMaxLength() {
    return DESCRIPTION_MAX_LENGTH
  }

  get params(): IRcsUseCaseStepParams {
    const params: IRcsUseCaseStepParams = {
      use_case_description: this._description,
      use_case_video_url: this._videoUrl || undefined,
    }

    if (!this.isUsFlow) return params

    return {
      ...params,
      use_case: this._useCase || undefined,
      monthly_trafic: this._toOptionalNumber(this._monthlyTraffic),
      have_shortcode_trafic: this.isShortcodeFlow,
      shortcode: this.isShortcodeFlow ? this._shortcode || undefined : undefined,
      shortcode_trafic: this.isShortcodeFlow
        ? this._toOptionalNumber(this._shortcodeTraffic)
        : undefined,
      traffic_volume: this._toOptionalNumber(this._trafficVolume),
    }
  }

  get isNextDisabled() {
    if (this._videoLoading) return true

    return !(
      this._isDescriptionValid &&
      this._isVideoValid &&
      this._isUseCaseValid &&
      this._isMonthlyTrafficValid &&
      this._isHasShortcodeValid &&
      this._isShortcodeValid &&
      this._isShortcodeTrafficValid &&
      this._isTrafficVolumeValid
    )
  }

  setDescription = (value: string) => {
    this._description = value
    if (this._descriptionError) this._descriptionError = ''
  }

  setUseCase = (value: string) => {
    this._useCase = value
    if (this._useCaseError) this._useCaseError = ''
  }

  setMonthlyTraffic = (value: string) => {
    this._monthlyTraffic = this._normalizeDigits(value, TRAFFIC_MAX_DIGITS)
    if (this._monthlyTrafficError) this._monthlyTrafficError = ''
  }

  setHasShortcodeTraffic = (value: string) => {
    this._hasShortcodeTraffic = value
    if (this._hasShortcodeTrafficError) this._hasShortcodeTrafficError = ''

    if (!this.isShortcodeFlow) {
      this._shortcode = ''
      this._shortcodeError = ''
      this._shortcodeTraffic = ''
      this._shortcodeTrafficError = ''
    }
  }

  setShortcode = (value: string) => {
    this._shortcode = this._normalizeDigits(value, SHORTCODE_MAX_LENGTH)
    if (this._shortcodeError) this._shortcodeError = ''
  }

  setShortcodeTraffic = (value: string) => {
    this._shortcodeTraffic = this._normalizeDigits(value, TRAFFIC_MAX_DIGITS)
    if (this._shortcodeTrafficError) this._shortcodeTrafficError = ''
  }

  setTrafficVolume = (value: string) => {
    this._trafficVolume = this._normalizeDigits(value, TRAFFIC_MAX_DIGITS)
    if (this._trafficVolumeError) this._trafficVolumeError = ''
  }

  loadUseCases = () => {
    this._wizardStore.loadUseCases()
  }

  setVideo = (files: File[] | FileList) => {
    const [file] = Array.from(files)
    if (!file) return

    if (this._video || this._videoUrl) {
      this._videoError = VIDEO_LIMIT_ERROR
      return
    }

    const error = this._getVideoError(file)
    if (error) {
      this._videoError = error
      return
    }

    this._videoError = null
    this._video = file
    this._uploadVideo(file)
  }

  removeVideo = () => {
    this._video = null
    this._videoUrl = ''
    this._videoError = null
    this._videoLoading = false
    this._pendingVideoToken = ''
  }

  clearVideoError = () => {
    this._videoError = null
  }

  onVideoUploaded = (data: IResponseEventRcsVideoUploaded) => {
    if (data.token !== this._pendingVideoToken) return

    this._videoUrl = data.url
    this._videoLoading = false
    this._pendingVideoToken = ''
  }

  onVideoUploadError = (data: IResponseEventRcsVideoUploadError) => {
    if (data.token !== this._pendingVideoToken) return

    this._video = null
    this._videoLoading = false
    this._videoError = { desc: data.error || VIDEO_UPLOAD_ERROR.desc }
    this._pendingVideoToken = ''
  }

  private _uploadVideo = async (file: File) => {
    runInAction(() => {
      this._videoLoading = true
    })

    try {
      const {
        data: { link, token },
      } = await RcsApi.getRcsVideoUploadLink({
        name: file.name,
        content_type: this._getVideoContentType(file),
      })

      runInAction(() => {
        if (this._video !== file) return

        this._pendingVideoToken = token
      })

      await RcsApi.uploadFileToStorage(link.url, file, link.headers)
    } catch (error) {
      logger.error(error)

      runInAction(() => {
        if (this._video !== file) return

        this._video = null
        this._videoError = VIDEO_UPLOAD_ERROR
        this._videoLoading = false
        this._pendingVideoToken = ''
      })
    }
  }

  private _getVideoError = (file: File): IVideoError | null => {
    if (!this._isVideoTypeAllowed(file)) return VIDEO_TYPE_ERROR
    if (file.size > VIDEO_MAX_SIZE_BYTES) return VIDEO_SIZE_ERROR
    return null
  }

  private _isVideoTypeAllowed = (file: File) => {
    if (VIDEO_ALLOWED_TYPES.includes(file.type)) return true

    const extension = file.name.split('.').pop()?.toLowerCase()
    return extension ? VIDEO_ALLOWED_EXTENSIONS.includes(extension) : false
  }

  private _getVideoContentType = (file: File) => {
    if (VIDEO_ALLOWED_TYPES.includes(file.type)) return file.type

    const extension = file.name.split('.').pop()?.toLowerCase()
    return extension === 'mov' ? 'video/quicktime' : 'video/mp4'
  }

  private _normalizeDigits = (value: string, maxLength: number) =>
    value.replace(NON_DIGIT_REGEX, '').slice(0, maxLength)

  private _toOptionalNumber = (value: string) => (value === '' ? undefined : Number(value))

  private _formatThousands = (value: string) =>
    value === '' ? '' : Number(value).toLocaleString('en-US')

  private _isTrafficValueValid = (value: string, min: number) => {
    if (value === '') return false
    const numericValue = Number(value)
    return numericValue >= min && numericValue <= TRAFFIC_MAX
  }

  validateMonthlyTraffic = () => {
    this._monthlyTrafficError = this._isMonthlyTrafficValid
      ? ''
      : 'Enter the monthly organic website traffic'
  }

  validateShortcode = () => {
    this._shortcodeError = this._isShortcodeValid ? '' : 'Enter a valid short code'
  }

  validateShortcodeTraffic = () => {
    this._shortcodeTrafficError = this._isShortcodeTrafficValid
      ? ''
      : 'Enter the estimated monthly traffic'
  }

  validateTrafficVolume = () => {
    this._trafficVolumeError = this._isTrafficVolumeValid
      ? ''
      : 'Enter the expected monthly traffic volume'
  }

  initErrors = (errors: INormalizedApiError) => {
    this._descriptionError = errors.getError('use_case_description')

    const videoError = errors.getError('use_case_video_url')
    this._videoError = videoError ? { desc: videoError } : null

    this._useCaseError = errors.getError('use_case')
    this._monthlyTrafficError = errors.getError('monthly_trafic')
    this._hasShortcodeTrafficError = errors.getError('have_shortcode_trafic')
    this._shortcodeError = errors.getError('shortcode')
    this._shortcodeTrafficError = errors.getError('shortcode_trafic')
    this._trafficVolumeError = errors.getError('traffic_volume')
  }

  init = (data: IResponseRcsCompliance) => {
    const useCase = data.data?.use_case

    this._description = useCase?.use_case_description ?? ''
    this._videoUrl = useCase?.use_case_video_url ?? ''
    this._useCase = useCase?.use_case?.key ?? ''
    if (this._useCase) this.loadUseCases()
    this._monthlyTraffic = String(useCase?.monthly_trafic ?? '')
    this._shortcode = useCase?.shortcode ?? ''
    this._shortcodeTraffic = String(useCase?.shortcode_trafic ?? '')
    this._trafficVolume = String(useCase?.traffic_volume ?? '')

    if (useCase?.have_shortcode_trafic === true) this._hasShortcodeTraffic = 'yes'
    else if (useCase?.have_shortcode_trafic === false) this._hasShortcodeTraffic = 'no'
    else this._hasShortcodeTraffic = ''
  }

  reset = () => {
    this._description = ''
    this._descriptionError = ''
    this._video = null
    this._videoUrl = ''
    this._videoError = null
    this._videoLoading = false
    this._pendingVideoToken = ''
    this._useCase = ''
    this._useCaseError = ''
    this._monthlyTraffic = ''
    this._monthlyTrafficError = ''
    this._hasShortcodeTraffic = ''
    this._hasShortcodeTrafficError = ''
    this._shortcode = ''
    this._shortcodeError = ''
    this._shortcodeTraffic = ''
    this._shortcodeTrafficError = ''
    this._trafficVolume = ''
    this._trafficVolumeError = ''
  }
}
